import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Save, ShieldCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/AuthContext';
import { isAtLeast } from '@/lib/roles';
import { FEATURE_DEFINITIONS, ROLE_KEYS, defaultFeaturePermissions } from '@/lib/featurePermissions';
import { useFeaturePermissions } from '@/lib/FeaturePermissionContext';
import AppHeader from '@/components/drill/AppHeader';
import { toast } from '@/components/ui/use-toast';

const ROLE_LABELS = { rig: 'Rig', supervisor: 'Supervisor', admin: 'Admin', owner: 'Owner' };

export default function FeaturePermissions() {
  const { effectiveRole } = useAuth();
  const { matrix, saving, updateMatrix } = useFeaturePermissions();
  const [draft, setDraft] = useState(matrix);
  React.useEffect(() => setDraft(matrix), [matrix]);

  const categories = useMemo(() => [...new Set(FEATURE_DEFINITIONS.map((feature) => feature.category))], []);
  if (!isAtLeast(effectiveRole, 'admin')) return <Navigate to="/" replace />;

  const toggle = (feature, role) => {
    if (!isAtLeast(role, feature.minRole)) return;
    setDraft((prev) => ({ ...prev, [feature.key]: { ...prev[feature.key], [role]: !prev[feature.key]?.[role] } }));
  };

  const save = async () => {
    try {
      await updateMatrix(draft);
      toast({ title: 'Feature permissions saved' });
    } catch (error) {
      toast({ title: 'Save failed', description: error?.message || 'Unable to save permissions.', variant: 'destructive' });
    }
  };

  const reset = () => setDraft(defaultFeaturePermissions());

  return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
    <AppHeader title="Feature Permissions" subtitle="Control which roles can use each DLP feature" backTo="/" />
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-24">
      <div className="rounded-2xl border bg-white p-5 dark:bg-slate-900 dark:border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div><h2 className="text-lg font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Feature access</h2><p className="text-sm text-slate-500 mt-1">Admin and Owner control the organisation-wide feature permissions. Minimum-role restrictions cannot be lowered.</p></div>
          <div className="flex gap-2"><Button variant="outline" className="rounded-xl" onClick={reset}>Reset defaults</Button><Button className="rounded-xl gap-2" onClick={save} disabled={saving}><Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save permissions'}</Button></div>
        </div>
        <div className="overflow-x-auto rounded-xl border dark:border-slate-800">
          <table className="w-full min-w-[760px] text-sm">
            <thead><tr className="bg-slate-50 dark:bg-slate-800"><th className="text-left p-3 font-semibold">Feature</th>{ROLE_KEYS.map((role) => <th key={role} className="p-3 font-semibold text-center">{ROLE_LABELS[role]}</th>)}</tr></thead>
            <tbody>
              {categories.map((category) => <React.Fragment key={category}>
                <tr className="bg-slate-100/70 dark:bg-slate-800/70"><td colSpan={5} className="p-2.5 font-semibold text-slate-600 dark:text-slate-300">{category}</td></tr>
                {FEATURE_DEFINITIONS.filter((feature) => feature.category === category).map((feature) => <tr key={feature.key} className="border-t dark:border-slate-800"><td className="p-3"><div className="font-medium">{feature.label}</div><div className="text-xs text-slate-400 mt-0.5">Minimum role: {ROLE_LABELS[feature.minRole]}</div></td>{ROLE_KEYS.map((role) => { const enabled = draft?.[feature.key]?.[role] !== false; const allowed = isAtLeast(role, feature.minRole); return <td key={role} className="p-3 text-center"><Checkbox checked={allowed && enabled} disabled={!allowed} onCheckedChange={() => toggle(feature, role)} /></td>; })}</tr>)}
              </React.Fragment>)}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  </div>;
}
